import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import {
  TopupAccountTableQuery,
  TopupAccountDto,
  TopupAccountDetailDto,
  TopupTransactionDto,
  TopupTransactionTableQuery,
  TopupHoldDto,
  RequestAdjustTopupBalancePayload,
  AdjustTopupBalanceResponse
} from '../../../models/cityparking/topup.model';
import { PagedResultDto } from '../../../models/table.model';

@Injectable({
  providedIn: 'root',
})
export class ApiTopupService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  // ================= API Functions =================

  /**
   * Fetch a list of Topup Accounts for the table
   */
  getTopupAccountForTable(query: TopupAccountTableQuery): Observable<PagedResultDto<TopupAccountDto>> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      userId: query.userId,
      menuId: query.menuId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;

    return this.apiMethodService.request<PagedResultDto<TopupAccountDto>>(
      '/Backoffice/getTopupAccountForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getTopupAccountForTable',
        token,
        headerOnly: false,
        params,
      }
    )
  }

  /**
   * Fetch a Topup Account by ID
   */
  getTopupAccountById(id: number, userId: number, menuId: number): Observable<TopupAccountDetailDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<TopupAccountDetailDto>(
      '/Backoffice/getTopupAccountById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getTopupAccountById',
        token,
        headerOnly: false,
        params: { id, userId, menuId },
      }
    )
  }

  /**
   * Fetch Transactions by Account ID with pagination
   */
  getTopupTransactionsByAccountId(
    topupAccountId: number,
    page: number = 1,
    pageSize: number = 20
  ): Observable<PagedResultDto<TopupTransactionDto>> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<PagedResultDto<TopupTransactionDto>>(
      '/Backoffice/getTopupTransactionsByAccountId',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getTopupTransactionsByAccountId',
        token,
        headerOnly: false,
        params: { topupAccountId, page, pageSize },
      }
    )
  }

  /**
   * Fetch Active Holds by Account ID with pagination
   */
  getTopupHoldsByAccountId(
    topupAccountId: number,
    page: number = 1,
    pageSize: number = 20
  ): Observable<PagedResultDto<TopupHoldDto>> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<PagedResultDto<TopupHoldDto>>(
      '/Backoffice/getTopupHoldsByAccountId',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getTopupHoldsByAccountId',
        token,
        headerOnly: false,
        params: { topupAccountId, page, pageSize },
      }
    )
  }

  /**
   * Fetch All Transactions for the global transaction table (requires date ranges)
   */
  getTopupTransactionForTable(query: TopupTransactionTableQuery): Observable<PagedResultDto<TopupTransactionDto>> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      startDate: query.startDate,
      endDate: query.endDate,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.topupMethod !== undefined && query.topupMethod !== null) params['topupMethod'] = query.topupMethod;
    if (query.minAmount !== undefined && query.minAmount !== null) params['minAmount'] = query.minAmount;
    if (query.maxAmount !== undefined && query.maxAmount !== null) params['maxAmount'] = query.maxAmount;
    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;

    return this.apiMethodService.request<PagedResultDto<TopupTransactionDto>>(
      '/Backoffice/getTopupTransactionForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getTopupTransactionForTable',
        token,
        headerOnly: false,
        params,
      }
    )
  }

  /**
   * Command to request balance adjustments (POST)
   */
  requestAdjustTopupAccountBalance(payload: RequestAdjustTopupBalancePayload): Observable<AdjustTopupBalanceResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<AdjustTopupBalanceResponse>(
      '/Backoffice/requestAdjustTopupAccountBalance',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'requestAdjustTopupAccountBalance',
        token,
        headerOnly: false,
        body: payload,
      }
    )
  }

  // ================= Mock Functions =================

  // private getMockAccountsForTable(query: TopupAccountTableQuery): Observable<PagedResultDto<TopupAccountDto>> {
  //   let mockList: TopupAccountDto[] = [
  //     { id: 10, topupNumber: 'TP-000101', amount: 1500.50, fullName: 'สมชาย ใจดี', phone: '0812345678', createDate: '2026-06-01T10:30:00', canAdjustBalance: true },
  //     { id: 20, topupNumber: 'TP-000102', amount: 250.00, fullName: 'สมศรี ดีใจ', phone: '0898765432', createDate: '2026-06-02T11:00:00', canAdjustBalance: true },
  //     { id: 30, topupNumber: 'TP-000103', amount: 50.00, fullName: 'วิชัย รักดี', phone: '0861112222', createDate: '2026-06-03T09:15:00', canAdjustBalance: false },
  //     { id: 40, topupNumber: 'TP-000104', amount: 4890.00, fullName: 'มานะ ขยัน', phone: '0823334444', createDate: '2026-06-04T16:45:00', canAdjustBalance: true },
  //     { id: 50, topupNumber: 'TP-000105', amount: 0.00, fullName: 'ปิติ ร่าเริง', phone: '0845556666', createDate: '2026-06-05T14:20:00', canAdjustBalance: true },
  //   ];

  //   if (query.searchText) {
  //     const term = query.searchText.toLowerCase();
  //     mockList = mockList.filter(x => 
  //       x.fullName.toLowerCase().includes(term) || 
  //       x.phone.includes(term) || 
  //       x.topupNumber.toLowerCase().includes(term)
  //     );
  //   }

  //   const total = mockList.length;
  //   return of({
  //     data: mockList,
  //     total: total
  //   });
  // }

  // private getMockAccountById(id: number, userId: number, menuId: number): Observable<TopupAccountDetailDto> {
  //   const accounts = [
  //     { id: 10, topupNumber: 'TP-000101', amount: 1500.50, fullName: 'สมชาย ใจดี', phone: '0812345678', createDate: '2026-06-01T10:30:00', canAdjustBalance: true },
  //     { id: 20, topupNumber: 'TP-000102', amount: 250.00, fullName: 'สมศรี ดีใจ', phone: '0898765432', createDate: '2026-06-02T11:00:00', canAdjustBalance: true },
  //     { id: 30, topupNumber: 'TP-000103', amount: 50.00, fullName: 'วิชัย รักดี', phone: '0861112222', createDate: '2026-06-03T09:15:00', canAdjustBalance: false },
  //     { id: 40, topupNumber: 'TP-000104', amount: 4890.00, fullName: 'มานะ ขยัน', phone: '0823334444', createDate: '2026-06-04T16:45:00', canAdjustBalance: true },
  //     { id: 50, topupNumber: 'TP-000105', amount: 0.00, fullName: 'ปิติ ร่าเริง', phone: '0845556666', createDate: '2026-06-05T14:20:00', canAdjustBalance: true },
  //   ];
  //   const acc = accounts.find(x => x.id === id) || accounts[0];

  //   const mockTransactions: TopupTransactionDto[] = [
  //     { id: 1001, topupAccountId: acc.id, topupNumber: acc.topupNumber, fullName: acc.fullName, phone: acc.phone, paidDate: '2026-06-05T10:30:00', amount: 500, balance: acc.amount, topupMethod: 1, description: 'Top Up', remark: 'เติมเงินผ่านธนาคาร', transactionNo: 'TXN-998822', referenceCode: 'REF-001', feeName: '', isPendingApproval: false },
  //     { id: 1002, topupAccountId: acc.id, topupNumber: acc.topupNumber, fullName: acc.fullName, phone: acc.phone, paidDate: '2026-06-05T11:45:00', amount: 150, balance: acc.amount - 150, topupMethod: 2, description: 'Parking Fee', remark: 'ชำระค่าจอดรถ', transactionNo: 'TXN-998823', referenceCode: 'REF-002', feeName: 'Normal Fee', isPendingApproval: false },
  //     { id: 1003, topupAccountId: acc.id, topupNumber: acc.topupNumber, fullName: acc.fullName, phone: acc.phone, paidDate: '2026-06-06T15:20:00', amount: 100, balance: acc.amount - 50, topupMethod: 3, description: 'Balance Adjustment (Increase)', remark: 'ปรับยอดเพิ่มจากแอดมิน', transactionNo: 'TXN-998824', referenceCode: 'REF-003', feeName: '', isPendingApproval: false }
  //   ];

  //   return of({
  //     ...acc,
  //     recentTransactions: mockTransactions
  //   });
  // }

  // private getMockTransactionsByAccountId(
  //   topupAccountId: number,
  //   page: number,
  //   pageSize: number
  // ): Observable<PagedResultDto<TopupTransactionDto>> {
  //   const list: TopupTransactionDto[] = [
  //     { id: 1001, topupAccountId, topupNumber: 'TP-000101', fullName: 'สมชาย ใจดี', phone: '0812345678', paidDate: '2026-06-05T10:30:00', amount: 500, balance: 1500, topupMethod: 1, description: 'Top Up', remark: 'เติมเงินผ่าน QR', transactionNo: 'TXN-998822', referenceCode: 'REF-001', feeName: '', isPendingApproval: false },
  //     { id: 1002, topupAccountId, topupNumber: 'TP-000101', fullName: 'สมชาย ใจดี', phone: '0812345678', paidDate: '2026-06-05T11:45:00', amount: 150, balance: 1350, topupMethod: 2, description: 'Parking Fee', remark: 'ชำระค่าบริการ', transactionNo: 'TXN-998823', referenceCode: 'REF-002', feeName: 'Normal Fee', isPendingApproval: false },
  //     { id: 1003, topupAccountId, topupNumber: 'TP-000101', fullName: 'สมชาย ใจดี', phone: '0812345678', paidDate: '2026-06-06T15:20:00', amount: 100, balance: 1450, topupMethod: 3, description: 'Balance Adjustment (Increase)', remark: 'ปรับปรุงยอด', transactionNo: 'TXN-998824', referenceCode: 'REF-003', feeName: '', isPendingApproval: true }
  //   ];
  //   return of({
  //     data: list,
  //     total: list.length
  //   });
  // }

  // private getMockHoldsByAccountId(
  //   topupAccountId: number,
  //   page: number,
  //   pageSize: number
  // ): Observable<PagedResultDto<TopupHoldDto>> {
  //   const list: TopupHoldDto[] = [
  //     { id: 5001, topupAccountId, name: 'จองสิทธิ์ที่จอดรถล่วงหน้า', amount: 100.00, remark: 'Hold สำหรับลานจอด A', createDate: '2026-06-06T12:00:00', status: true }
  //   ];
  //   return of({
  //     data: list,
  //     total: list.length
  //   });
  // }

  // private getMockTransactionForTable(query: TopupTransactionTableQuery): Observable<PagedResultDto<TopupTransactionDto>> {
  //   let list: TopupTransactionDto[] = [
  //     { id: 1001, topupAccountId: 10, topupNumber: 'TP-000101', fullName: 'สมชาย ใจดี', phone: '0812345678', paidDate: '2026-06-05T10:30:00', amount: 500, balance: 1500, topupMethod: 1, description: 'Top Up', remark: 'เติมเงินผ่านธนาคาร', transactionNo: 'TXN-998822', referenceCode: 'REF-001', feeName: '', isPendingApproval: false },
  //     { id: 1002, topupAccountId: 10, topupNumber: 'TP-000101', fullName: 'สมชาย ใจดี', phone: '0812345678', paidDate: '2026-06-05T11:45:00', amount: 150, balance: 1350, topupMethod: 2, description: 'Parking Fee', remark: 'ชำระค่าจอดรถ', transactionNo: 'TXN-998823', referenceCode: 'REF-002', feeName: 'Normal Fee', isPendingApproval: false },
  //     { id: 1003, topupAccountId: 20, topupNumber: 'TP-000102', fullName: 'สมศรี ดีใจ', phone: '0898765432', paidDate: '2026-06-06T15:20:00', amount: 100, balance: 250, topupMethod: 3, description: 'Balance Adjustment (Increase)', remark: 'ปรับปรุงยอดโดยแอดมิน', transactionNo: 'TXN-998824', referenceCode: 'REF-003', feeName: '', isPendingApproval: true },
  //     { id: 1004, topupAccountId: 40, topupNumber: 'TP-000104', fullName: 'มานะ ขยัน', phone: '0823334444', paidDate: '2026-06-06T16:00:00', amount: 200, balance: 4890, topupMethod: 4, description: 'Balance Adjustment (Decrease)', remark: 'ปรับลดยอด', transactionNo: 'TXN-998825', referenceCode: 'REF-004', feeName: '', isPendingApproval: false }
  //   ];

  //   if (query.topupMethod) {
  //     list = list.filter(x => x.topupMethod === query.topupMethod);
  //   }
  //   if (query.searchText) {
  //     const term = query.searchText.toLowerCase();
  //     list = list.filter(x => 
  //       x.fullName.toLowerCase().includes(term) || 
  //       x.phone.includes(term) || 
  //       x.topupNumber.toLowerCase().includes(term)
  //     );
  //   }

  //   return of({
  //     data: list,
  //     total: list.length
  //   });
  // }

  // private getMockAdjustResponse(payload: RequestAdjustTopupBalancePayload): Observable<AdjustTopupBalanceResponse> {
  //   // If remark is missing, error
  //   if (!payload.remark) {
  //     return of({
  //       isSuccess: false,
  //       message: 'กรุณาระบุหมายเหตุ',
  //       messageEn: 'Please provide a remark.',
  //       requiresApproval: false,
  //       approvalRequestId: 0,
  //       topupAccountId: payload.topupAccountId,
  //       topupTransactionId: 0
  //     });
  //   }

  //   // Default to sending for approval or direct success
  //   return of({
  //     isSuccess: true,
  //     message: 'บันทึกรายการและส่งขออนุมัติเรียบร้อย',
  //     messageEn: 'Transaction recorded and approval request submitted.',
  //     requiresApproval: true,
  //     approvalRequestId: 99,
  //     topupAccountId: payload.topupAccountId,
  //     topupTransactionId: 9901
  //   });
  // }
}
